/**
 * Recover from a stale-build chunk load failure (#1453).
 *
 * Vite emits `vite:preloadError` on `window` when a dynamic import's chunk (or
 * one of its CSS/JS dependencies) fails to load. After a deploy that is almost
 * always because the tab is still running the previous build and the hashed
 * chunk it asked for has been replaced. The fix is a reload, which fetches the
 * current index.html and therefore the current chunk set.
 *
 * The reload is rate-limited through sessionStorage so a genuinely broken
 * build (or an offline tab) cannot loop: within RELOAD_COOLDOWN_MS of a reload
 * we let the error propagate to the ErrorBoundary instead.
 */

export const RELOAD_COOLDOWN_MS = 60_000;
const STORAGE_KEY = 'cc:chunk-reload-at';

export interface ChunkReloadDeps {
  target?: Pick<Window, 'addEventListener'>;
  reload?: () => void;
  now?: () => number;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
}

function readLastReload(storage: ChunkReloadDeps['storage']): number {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastReload(storage: ChunkReloadDeps['storage'], at: number): void {
  try {
    storage?.setItem(STORAGE_KEY, String(at));
  } catch {
    // Private mode or blocked storage: we still reload, we just cannot
    // remember that we did. The cooldown then relies on the error not recurring.
  }
}

/** Decide whether a preload error at `at` should trigger a reload. */
export function shouldReload(lastReloadAt: number, at: number): boolean {
  if (!lastReloadAt) return true; // never reloaded in this tab session
  return at - lastReloadAt > RELOAD_COOLDOWN_MS;
}

export function installChunkReload(deps: ChunkReloadDeps = {}): () => void {
  const target = deps.target ?? window;
  const reload = deps.reload ?? (() => window.location.reload());
  const now = deps.now ?? Date.now;
  const storage = deps.storage === undefined ? safeSessionStorage() : deps.storage;

  const handler = (event: Event) => {
    const at = now();
    if (!shouldReload(readLastReload(storage), at)) {
      return; // let the ErrorBoundary report it rather than loop
    }
    event.preventDefault();
    writeLastReload(storage, at);
    reload();
  };

  target.addEventListener('vite:preloadError', handler);
  return () => (target as Window).removeEventListener?.('vite:preloadError', handler);
}

function safeSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
